import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useMediaQuery } from '@material-ui/core';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import ToolIcon from 'core/assets/svg/tool.svg?react';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { ContextualToolsContext, useContextualTools, isContextualToolsPreview } from './preview-context';
import { getStaffPageContext } from './staff-context';
import { getPanelSession } from './sample-access';
import useStyles, { STAFF_DOCK_MIN_WIDTH } from './staff-preview.styles';

const Panel = lazy(() => import('./contextual-tools-panel'));

// Split out so its styles are only built when the preview is on (header tests use a partial theme).
function LauncherButton({ api }) {
  const classes = useStyles();
  return (
    <div ref={api.launcherRef} className={classes.launcherWrapper}>
      <button
        type="button"
        className={classNames(classes.launcher, { [classes.launcherOpen]: api.open })}
        onClick={api.toggle}
        aria-expanded={api.open}
        aria-controls={api.hasOpened ? 'staff-tools-panel' : undefined}
      >
        <ToolIcon className={classes.launcherIcon} aria-hidden="true" />
        Tools
      </button>
    </div>
  );
}

export function ContextualToolsLauncher() {
  const api = useContextualTools();
  return api?.allowed ? <LauncherButton api={api} /> : null;
}

const StaffToolsPreview = observer(({ children }) => {
  const classes = useStyles();
  const session = useSession();
  const store = useStore();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [guards, setGuards] = useState({});
  const hostRef = useRef();
  const launcherRef = useRef();
  const docked = useMediaQuery(`(min-width:${STAFF_DOCK_MIN_WIDTH}px)`);
  const allowed = session.isLoggedIn && session.canViewTools;
  const panelSession = useMemo(() => getPanelSession(session), [session]);
  const visible = open && allowed;
  const pageKey = location.pathname + location.search;
  const context = getStaffPageContext({ pathname: location.pathname, search: window.location.search, store, guards });
  const register = useCallback((token, guard) => {
    setGuards((current) => ({ ...current, [token]: guard }));
    return () =>
      setGuards((current) => {
        const next = { ...current };
        delete next[token];
        return next;
      });
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setHasOpened(true);
    setOpen((value) => !value);
  }, []);
  const api = useMemo(
    () => ({ allowed, open: visible, hasOpened, toggle, launcherRef, register }),
    [allowed, visible, hasOpened, toggle, register]
  );
  useEffect(() => {
    if (!allowed) {
      setOpen(false);
    }
  }, [allowed]);
  useEffect(() => {
    // Existing fixed back controls measure their position on viewport resize.
    window.dispatchEvent(new Event('resize'));
  }, [visible, docked]);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    host.inert = visible && !docked;
    if (host.inert) {
      host.setAttribute('aria-hidden', 'true');
    } else {
      host.removeAttribute('aria-hidden');
    }
    return () => {
      host.inert = false;
      host.removeAttribute('aria-hidden');
    };
  }, [visible, docked]);
  const wasVisible = useRef(false);
  useEffect(() => {
    if (!visible && wasVisible.current) {
      launcherRef.current?.querySelector('button')?.focus();
    }
    wasVisible.current = visible;
  }, [visible]);
  return (
    <ContextualToolsContext.Provider value={api}>
      <div
        ref={hostRef}
        className={`${classes.host} ${visible && docked ? classes.docked : ''}`}
        data-testid="staff-tools-host"
      >
        {children}
      </div>
      {visible && !docked && <div className={classes.backdrop} aria-hidden="true" onClick={close} />}
      <Suspense fallback={null}>
        {hasOpened && (
          <Panel
            session={panelSession}
            context={context}
            pageKey={pageKey}
            open={visible}
            onClose={close}
            docked={docked}
          />
        )}
      </Suspense>
    </ContextualToolsContext.Provider>
  );
});
export default StaffToolsPreview;

export function StaffToolsPreviewBoundary({ children }) {
  return isContextualToolsPreview() ? <StaffToolsPreview>{children}</StaffToolsPreview> : children;
}
